import { useCallback, useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppFormField, AppFormGrid } from "../../../components/layout";
import { AppAutocomplete, AppCheckbox, AppTextField } from "../../../components/form";
import { widgetService, type MappingFieldDescriptor } from "../../../services/widgetService";

type Props = {
  sourceIndex?: string;
  dslJson: string;
  onDslChange: (json: string) => void;
};

function buildDsl(
  selectedFields: string[],
  dateField: string | null,
  dateFrom: string,
  dateTo: string
): Record<string, unknown> {
  const filters: Record<string, unknown>[] = [];
  if (dateField && (dateFrom || dateTo)) {
    const range: Record<string, unknown> = {};
    if (dateFrom) range.gte = dateFrom;
    if (dateTo) range.lte = dateTo;
    filters.push({ range: { [dateField]: range } });
  }
  const query =
    filters.length === 0
      ? { match_all: {} }
      : filters.length === 1
        ? filters[0]
        : { bool: { filter: filters } };
  const dsl: Record<string, unknown> = { size: 2, query };
  if (selectedFields.length > 0) {
    dsl._source = selectedFields;
  }
  return dsl;
}

export default function PrepDslSimpleBuilder({ sourceIndex, dslJson, onDslChange }: Props) {
  const { t } = useTranslation();
  const [fields, setFields] = useState<MappingFieldDescriptor[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [dateField, setDateField] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [enabled, setEnabled] = useState(false);

  const fieldPaths = useMemo(() => fields.map((f) => f.path), [fields]);
  const dateFieldOptions = useMemo(() => fields.filter((f) => /date/i.test(f.type)).map((f) => f.path), [fields]);

  const loadHints = useCallback(async () => {
    const index = sourceIndex?.trim();
    if (!index) {
      setFields([]);
      return;
    }
    setLoading(true);
    try {
      const res = await widgetService.getMappingHints(index);
      setFields(res.fields || []);
    } catch {
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [sourceIndex]);

  useEffect(() => {
    loadHints().catch(console.error);
  }, [loadHints]);

  useEffect(() => {
    if (!enabled) return;
    const dsl = buildDsl(selectedFields, dateField, dateFrom, dateTo);
    onDslChange(JSON.stringify(dsl, null, 2));
  }, [enabled, selectedFields, dateField, dateFrom, dateTo, onDslChange]);

  return (
    <Stack spacing={1.5}>
      <AppCheckbox
        label={t("prep.dslBuilder.enable")}
        checked={enabled}
        onChange={(e) => setEnabled(e.target.checked)}
      />
      {enabled && (
        <>
          <Typography variant="caption" color="text.secondary">
            {t("prep.dslBuilder.hintsFor", { index: sourceIndex || "—" })}
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={fieldPaths}
            value={selectedFields}
            onChange={(_, v) => setSelectedFields(v)}
            loading={loading}
            disableCloseOnSelect
            renderInput={(params) => (
              <AppTextField {...params} label={t("prep.dslBuilder.sourceFields")} />
            )}
          />
          <AppAutocomplete
            label={t("prep.dslBuilder.dateField")}
            options={dateFieldOptions}
            value={dateField}
            onChange={(_, v) => setDateField(v)}
          />
          <AppFormGrid spacing="compact">
            <AppFormField size={{ xs: 12, sm: 6 }}>
              <AppTextField
                type="date"
                label={t("prep.dslBuilder.dateFrom")}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </AppFormField>
            <AppFormField size={{ xs: 12, sm: 6 }}>
              <AppTextField
                type="date"
                label={t("prep.dslBuilder.dateTo")}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </AppFormField>
          </AppFormGrid>
        </>
      )}
    </Stack>
  );
}
